export type RecipientTransferDocRequirement = {
  document: string;
  displayName: string;
  isRequired: boolean;
};

export type LeadRecipientUploadDoc = {
  id?: number;
  docName?: string | null;
  [key: string]: unknown;
};

export type RecipientTransferDocWithLead = RecipientTransferDocRequirement & {
  id: number;
  leadsData: LeadRecipientUploadDoc | null;
  leadId: number | null;
  docName: string;
  loading: boolean;
  error: string;
};

/**
 * Normalizes requirement `document` and lead `docName` for comparison:
 * underscores in lead names become spaces, then case/spacing is collapsed.
 */
export function normalizeRecipientDocLabelForCompare(label: string | null | undefined): string {
  return String(label ?? "")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function recipientRequirementMatchesLeadDocName(
  requirementDocument: string,
  leadDocName: string | null | undefined
): boolean {
  return (
    normalizeRecipientDocLabelForCompare(requirementDocument) ===
    normalizeRecipientDocLabelForCompare(leadDocName)
  );
}

export function mergeRecipientTransferDocsWithLeads(
  documentsList: RecipientTransferDocRequirement[],
  leadDocumentsList: LeadRecipientUploadDoc[]
): RecipientTransferDocWithLead[] {
  return documentsList.map((doc, index) => {
    const match =
      leadDocumentsList.find((lead) =>
        recipientRequirementMatchesLeadDocName(doc.document, String(lead.docName ?? ""))
      ) ?? null;
    const leadId = typeof match?.id === "number" && Number.isFinite(match.id) ? match.id : null;
    const docName = typeof match?.docName === "string" ? match.docName : "";

    return {
      ...doc,
      id: index,
      leadsData: match,
      leadId,
      docName,
      loading: false,
      error: "",
    };
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseRequirementRow(item: unknown): RecipientTransferDocRequirement | null {
  const row = asRecord(item);
  if (!row) return null;

  const document = String(row.document ?? "").trim();
  if (!document) return null;

  const displayName = String(row.displayName ?? row.display_name ?? document).trim() || document;
  const raw = row.isRequired;
  const isRequired =
    raw === true ||
    raw === "true" ||
    raw === 1 ||
    raw === "1" ||
    String(raw).toLowerCase() === "yes";

  return { document, displayName, isRequired };
}

function parseRequirementArray(items: unknown[]): RecipientTransferDocRequirement[] {
  const out: RecipientTransferDocRequirement[] = [];
  for (const item of items) {
    const row = parseRequirementRow(item);
    if (row) out.push(row);
  }
  return out;
}

/** Prefer API display label when lead `docName` is an internal doc type, not a real filename. */
export function uploadedDocumentDisplayName(docName: string, displayName?: string): string {
  const trimmed = docName?.trim() ?? "";
  if (!trimmed) return displayName ?? "";

  if (/\.[a-z0-9]{2,8}$/i.test(trimmed)) return trimmed;

  return displayName || trimmed;
}

export function parseRecipientTransferDocsListResponse(
  payload: unknown
): RecipientTransferDocRequirement[] {
  if (Array.isArray(payload)) {
    return parseRequirementArray(payload);
  }

  const root = asRecord(payload);
  if (!root) return [];

  const first = root.data;
  if (Array.isArray(first)) {
    return parseRequirementArray(first);
  }

  const firstObj = asRecord(first);
  if (firstObj) {
    const nestedArr = firstObj.data;
    if (Array.isArray(nestedArr)) {
      return parseRequirementArray(nestedArr);
    }
    const alt = firstObj.documents ?? firstObj.requirements ?? firstObj.recipientDocs;
    if (Array.isArray(alt)) {
      return parseRequirementArray(alt);
    }
  }

  return [];
}
